'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {AppIcon, FaExternalLinkIcon} from '../icon';
import {Typography} from '../primitives';
import {mapEmbedUrl, mapShareUrl, type MapCoordinates} from './mapUrls';

export type MapPanelAspectRatio = string | {mobile: string; desktop: string};
const DEFAULT_MAP_RATIO: MapPanelAspectRatio = {mobile: '339 / 250', desktop: '556 / 350'};
const OVERLAY_CLASS = 'MapPanel-overlay';

export interface MapPanelProps {
  coordinates?: MapCoordinates;
  zoom?: number;
  imageUrl?: string;
  label: string;
  href?: string;
  aspectRatio?: MapPanelAspectRatio;
  onClick?: () => void;
}

const Panel = styled('a', {shouldForwardProp: propName => propName !== '$mobile' && propName !== '$desktop'})<{
  $mobile: string;
  $desktop: string;
}>(({theme, $mobile, $desktop}) => ({
  position: 'relative',
  display: 'block',
  width: '100%',
  aspectRatio: $mobile,
  borderRadius: theme.radii.images,
  overflow: 'hidden',
  backgroundColor: theme.palette.colors.surface.neutral.disabled,
  textDecoration: 'none',
  [theme.breakpoints.up('md')]: {aspectRatio: $desktop},
  [`&:hover .${OVERLAY_CLASS}, &:focus-visible .${OVERLAY_CLASS}`]: {opacity: 1},
}));

const PanelImage = styled('img')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const PanelFrame = styled('iframe')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  border: 0,
  pointerEvents: 'none',
});

const Overlay = styled(Box)(({theme}) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.colors.surface.darkOverlay,
  opacity: 0,
  transition: theme.transitions.create('opacity'),
  '@media (hover: none)': {
    opacity: 1,
    backgroundColor: 'transparent',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    padding: theme.spacing(1.5),
  },
}));

const Pill = styled(Box)(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5, 2.5),
  borderRadius: theme.radii.full,
  backgroundColor: theme.palette.colors.surface.neutral.default,
  border: `1px solid ${theme.palette.colors.border.primary.default}`,
  color: theme.palette.colors.text.primary.default,
  '& svg': {display: 'none'},
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.colors.surface.darkOverlayStrong,
    color: theme.palette.colors.text.neutral.inverse,
    border: `1px solid ${theme.palette.colors.border.neutral.inverse}`,
    gap: theme.spacing(1),
    '& svg': {display: 'inline-block'},
  },
}));

export function MapPanel({coordinates, zoom, imageUrl, label, href, aspectRatio = DEFAULT_MAP_RATIO, onClick}: MapPanelProps) {
  const {t} = useTranslation();
  const ratio = typeof aspectRatio === 'string' ? {mobile: aspectRatio, desktop: aspectRatio} : aspectRatio;
  const mapsLabel = t('view_on_google_maps');
  const resolvedHref = href ?? (coordinates ? mapShareUrl(coordinates) : undefined);

  return (
    <Panel
      as={resolvedHref ? 'a' : 'div'}
      $mobile={ratio.mobile}
      $desktop={ratio.desktop}
      href={resolvedHref}
      target={resolvedHref ? '_blank' : undefined}
      rel={resolvedHref ? 'noopener noreferrer' : undefined}
      aria-label={resolvedHref ? `${label} — ${mapsLabel}` : undefined}
      onClick={resolvedHref ? onClick : undefined}
    >
      {coordinates ? (
        <PanelFrame
          src={mapEmbedUrl(coordinates, zoom)}
          title={label}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          aria-hidden
          tabIndex={-1}
        />
      ) : (
        imageUrl && <PanelImage src={imageUrl} alt={label} />
      )}
      {resolvedHref && (
        <Overlay className={OVERLAY_CLASS} aria-hidden>
          <Pill>
            <Typography
              size="body-2"
              weight="medium"
              mobileSize="caption"
              mobileWeight="medium"
              sx={{letterSpacing: {xs: '0.02em', md: '-0.015em'}, color: 'inherit'}}
            >
              {mapsLabel}
            </Typography>
            <AppIcon icon={FaExternalLinkIcon} aria-hidden />
          </Pill>
        </Overlay>
      )}
    </Panel>
  );
}

MapPanel.displayName = 'MapPanel';
